import React, { useState } from 'react';
import { Company, ScrapeJob } from '../../types';
import { AddCompanyModal } from './AddCompanyModal';
import { EditCompanyModal } from './EditCompanyModal';
import { ImportTosdrModal } from './ImportTosdrModal';
import { TriggerScrapeButton } from './TriggerScrapeButton';
import { SystemHealthCard } from './SystemHealthCard';
import { useAdmin } from '../../hooks/useAdmin';
import { Plus, Database, RefreshCw, Edit2, Download } from 'lucide-react';

interface AdminPanelProps {
  companies: Company[];
  onRefresh: () => void;
  onJobCreated?: (job: ScrapeJob) => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ companies, onRefresh, onJobCreated }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isTosdrModalOpen, setIsTosdrModalOpen] = useState<boolean>(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [adminKey, setAdminKey] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tos_admin_key') || 'dev-secret-admin-key';
    }
    return 'dev-secret-admin-key';
  });

  const { seedDefaultCatalog, isSeeding, error } = useAdmin(adminKey);

  const handleSeed = async () => {
    const success = await seedDefaultCatalog();
    if (success) {
      onRefresh();
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Action Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Admin & Ingestion Console
            </h1>
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-bold">
              v1.2.0
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Configure tracked companies, policy endpoints, and execute manual scraping triggers
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsTosdrModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-2xs"
            title="Import verified tech services from ToS;DR directory"
          >
            <Download className="w-4 h-4" />
            Import ToS;DR Directory
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-2xs"
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>

          <button
            onClick={handleSeed}
            disabled={isSeeding}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-50"
            title="Seed default 7 companies"
          >
            <Database className="w-4 h-4" />
            {isSeeding ? 'Seeding...' : 'Seed Catalog'}
          </button>
        </div>
      </div>

      {/* System Health Overview Card */}
      <SystemHealthCard />

      {/* Tracked Companies Management Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/80 overflow-hidden shadow-2xs">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="font-bold text-slate-900 dark:text-white text-base">
            Tracked Companies ({companies.length})
          </h2>
          <button
            onClick={onRefresh}
            className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 font-semibold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="py-3 px-4">Company</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Terms of Service URL</th>
                <th className="py-3 px-4">Privacy Policy URL</th>
                <th className="py-3 px-4 text-center">Revisions</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {companies.map((c) => {
                const policies = c.trackedPolicies || c.tracked_policies || {};
                const tosUrl = policies.terms_of_service?.url || '—';
                const privUrl = policies.privacy_policy?.url || '—';
                const revCount = c.totalRevisions || c.total_revisions || c.revisionCounts?.total || 2;

                return (
                  <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        <span>{c.name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">({c.id})</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{c.category}</td>
                    <td className="py-3 px-4 max-w-xs truncate text-slate-500 font-mono text-[11px]">
                      {tosUrl}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate text-slate-500 font-mono text-[11px]">
                      {privUrl}
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-slate-700 dark:text-slate-300 font-mono">
                      {revCount}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <TriggerScrapeButton
                        companyId={c.id}
                        policyType="terms_of_service"
                        compact={true}
                        onSuccess={onJobCreated}
                      />
                      <button
                        onClick={() => setEditingCompany(c)}
                        className="p-1.5 rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-600 dark:text-slate-300"
                        title="Edit Company"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {isAddModalOpen && (
        <AddCompanyModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            onRefresh();
          }}
        />
      )}

      {editingCompany && (
        <EditCompanyModal
          company={editingCompany}
          isOpen={Boolean(editingCompany)}
          onClose={() => setEditingCompany(null)}
          onSuccess={() => {
            setEditingCompany(null);
            onRefresh();
          }}
        />
      )}

      {isTosdrModalOpen && (
        <ImportTosdrModal
          isOpen={isTosdrModalOpen}
          onClose={() => setIsTosdrModalOpen(false)}
          onSuccess={() => {
            setIsTosdrModalOpen(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};
